import * as THREE from "three";
import { Bezier } from "bezier-js";

// 베지어 곡선 수정모드 ------------------------------------------------------------
// 비는 번호들은 원본 코드 PotreeViewer3.jsx 에서 관리

// DouglasPeucker 베지어 곡선 단순화
const simplifyDouglasPeucker = (points, epsilon) => {
  if (points.length < 2) return points;

  const [start, end] = [points[0], points[points.length - 1]];

  const calcPerpDistance = ([px, py], [sx, sy], [ex, ey]) => {
    const area = Math.abs(
      sx * ey + ex * py + px * sy - (sy * ex + ey * px + py * sx)
    );
    const base = Math.hypot(ex - sx, ey - sy);
    return base === 0 ? 0 : area / base;
  };

  let maxDist = -1;
  let index = -1;

  for (let i = 1; i < points.length - 1; i += 1) {
    const dist = calcPerpDistance(points[i], start, end);
    if (dist > maxDist) {
      maxDist = dist;
      index = i;
    }
  }

  if (maxDist > epsilon) {
    const left = simplifyDouglasPeucker(points.slice(0, index + 1), epsilon);
    const right = simplifyDouglasPeucker(points.slice(index), epsilon);
    return [...left.slice(0, -1), ...right];
  }

  return [start, end];
};

// 1. 최종 경로를 받아서 하나의 경로로 병합 & 베지어 곡선으로 변환
export const converterToBezier = (fullPath) => {
  const segments = [];
  let sharedEndPoint = null;
  let epsilon = 0.1; // 숫자가 작아질수록 촘촘하다

  // 각 코너 배열을 합쳐놓은 전체 경로 배열
  fullPath.features.forEach((feature, featureIdx) => {
    // const coords = feature.geometry.coordinates; // 코너를 표현하는 배열
    const coords = simplifyDouglasPeucker(
      feature.geometry.coordinates,
      epsilon
    );
    const points = [];

    if (sharedEndPoint) {
      // 1) 이전 코너의 끝점을 첫 슬롯에 추가
      points.push(sharedEndPoint);
    }

    // 각 코너 좌표 배열을 객체 배열로 변환
    coords.forEach(([x, y], idx) => {
      const pointObj = { x, y };

      // 첫 좌표는 두 번째 슬롯부터 들어가므로 데이터 그대로 보존
      points.push(pointObj);

      // 마지막 코너 좌표의 경우 공유 좌표 업데이트
      if (idx === coords.length - 1) {
        sharedEndPoint = pointObj;
      }
    }); // 각 코너 좌표들을 객체로 변환
    segments.push({
      points,
      bezier: new Bezier(points),
      featureIndex: featureIdx,
    });
  });

  return segments;
};

// 3. 제어점 시각화를 위한 3D 요소 생성
export const visualizeBezierControlPoints = (
  bezierCurves,
  potreeViewer,
  getMinZ
) => {
  // 제어점 3D 요소 생성 및 시각화
  const scene = potreeViewer.scene.scene;
  const controlGrup = new THREE.Group();
  controlGrup.name = "bezier-controls";

  bezierCurves.forEach((segment) => {
    // bezierCurves = [...{ points: [...{x: number, y: number}], bezier: Bezier, featureIndex: number }]
    segment.points.forEach((point, pIdx) => {
      // point = { x: number, y: number } > 각 코너 좌표들
      // 첫 점은 이전 코너의 끝점과 중복이므로 제외하고 두번째부터 시각화를 진행한다.
      //   if (pIdx === 0) return;
      const geometry = new THREE.SphereGeometry(0.2, 32, 32);
      const material = new THREE.MeshBasicMaterial({
        color: 0xff0000,
        depthTest: false,
      });
      const sphere = new THREE.Mesh(geometry, material);
      sphere.position.set(point.x, point.y, getMinZ() + 1);

      sphere.userData = {
        featureIndex: segment.featureIndex,
        pointIndex: pIdx,
        isControlPoint: true,
      };

      sphere.renderOrder = 999;
      controlGrup.add(sphere);
    });
  });

  scene.add(controlGrup);
  //   updatePathVisualization(bezierCurves, potreeViewer, getMinZ);
};

// 4. 베지어 곡선 경로 시각화
// export const updatePathVisualization = (
//   bezierCurves,
//   potreeViewer,
//   getMinZ
// ) => {
//   // bezierCurves = [...{ points: [...{x: number, y: number}], bezier: Bezier, featureIndex: number }]
//   if (!potreeViewer) return;

//   const scene = potreeViewer.scene.scene;
//   // 기존 경로 제거
//   const oldPath = scene.getObjectByName("bezier-path-line");
//   if (oldPath) scene.remove(oldPath);
//   // 새로운 경로 생성
//   const pathGroup = new THREE.Group();
//   pathGroup.name = "bezier-path-line";

//   bezierCurves.forEach((segment) => {
//     // segment = { points: [...{x: number, y: number}], bezier: Bezier, featureIndex: number }
//     const length = segment.bezier.length();
//     const minSamples = 3;
//     const maxSamples = 20;
//     const base = 2;
//     const coefficient = 0.2;
//     const samples = Math.max(
//       minSamples,
//       Math.min(maxSamples, Math.round(base + coefficient * length))
//     );

//     const lut = segment.bezier.getLUT(samples); // 베지어 곡선을 N개의 점으로 변환
//     const points = lut.map(
//       (p) => new THREE.Vector3(p.x, p.y, (p.z ?? 0) + getMinZ() + 1)
//     );
//     const geometry = new THREE.BufferGeometry().setFromPoints(points);
//     const material = new THREE.LineBasicMaterial({
//       color: 0x00ffff,
//       linewidth: 3,
//     });
//     const line = new THREE.Line(geometry, material);
//     pathGroup.add(line);
//   });

//   scene.add(pathGroup);
// };

// 6. 베지어 곡선 업데이트
export const updateBezierCurve = (
  featureIndex,
  pointIndex,
  newPosition,
  setBezierCurves,
  potreeViewer,
  getMinZ
) => {
  setBezierCurves((prevCurves) => {
    // prevCurve = [...{ points: [...{x: number, y: number}], bezier: Bezier, featureIndex: number }]
    const current = prevCurves[featureIndex];
    if (!current?.points) return prevCurves;

    const updatedPoint = { x: newPosition.x, y: newPosition.y };

    // 이전 곡선 정보 복사
    const nextCurves = prevCurves.map((segment) => ({
      ...segment,
      points: segment.points.map((p) => ({ ...p })),
    }));

    // 현재 세그먼트 업데이트
    const currentSegment = nextCurves[featureIndex];
    currentSegment.points[pointIndex] = {
      ...currentSegment.points[pointIndex],
      ...updatedPoint,
    };

    // 0번 포인트 인덱스를 옮길 경우  이전 인덱스의 마지막 점과 동일한 좌표로 업데이트
    if (featureIndex > 0 && pointIndex === 0) {
      const prevSegment = nextCurves[featureIndex - 1];
      const lastIdx = prevSegment.points.length - 1;
      prevSegment.points[lastIdx] = {
        ...prevSegment.points[lastIdx],
        ...updatedPoint,
      };
      prevSegment.bezier = new Bezier(prevSegment.points);
    }

    // 마지막 포인트 인덱스를 옮길 경우  다음 인덱스의 첫 점과 동일한 좌표로 업데이트
    if (
      featureIndex < nextCurves.length - 1 &&
      pointIndex === currentSegment.points.length - 1
    ) {
      const nextSegment = nextCurves[featureIndex + 1];
      nextSegment.points[0] = {
        ...nextSegment.points[0],
        ...updatedPoint,
      };
      nextSegment.bezier = new Bezier(nextSegment.points);
    }

    currentSegment.bezier = new Bezier(currentSegment.points);

    // updatePathVisualization(nextCurves, potreeViewer, getMinZ);
    return nextCurves;
  });
};
// ------------------------------------------------------------
// 7. 베지어 곡선을 좌표로 변환
export const bezierToCoords = (bezierCurves, samplePerCurve = 50) => {
  const allCoords = [];

  bezierCurves.forEach(({ bezier }) => {
    const lut = bezier.getLUT(samplePerCurve);
    lut.forEach((point) => {
      allCoords.push([point.x, point.y]);
    });
  });

  return allCoords;
};
// ------------------------------------------------------------

export const visualizePoints = (coords, potreeViewer, getMinZ) => {
  if (!coords || coords.length === 0) {
    console.warn("No coordinates provided.");
    return;
  }

  const scene = potreeViewer.scene.scene;

  // 기존 그룹 제거 (중복 방지)
  const existing = scene.getObjectByName("simple-point-visualization");
  if (existing) {
    scene.remove(existing);
  }

  const group = new THREE.Group();
  group.name = "simple-point-visualization";

  coords.forEach((pt, idx) => {
    const [x, y] = pt;

    const geometry = new THREE.SphereGeometry(0.15, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      depthTest: false,
    });

    const sphere = new THREE.Mesh(geometry, material);

    sphere.position.set(x, y, getMinZ() + 1);

    sphere.userData = {
      index: idx,
      type: "simple-visual",
    };

    sphere.renderOrder = 999;
    group.add(sphere);
  });

  scene.add(group);
};
